(() => {
  if (customElements.get('kidsbazar-benefits')) return;

  class KidsBazarBenefits extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.track = this.querySelector('[data-benefits-track]');
      this.dotsContainer = this.querySelector('[data-benefits-dots]');
      this.cards = this.track ? Array.from(this.track.querySelectorAll('[data-benefit-card]')) : [];
      this.autoSlide = this.dataset.autoplay === 'true';
      this.speed = 16 + (Number(this.dataset.speed) || 3) * 4; // continuous pixels per second
      this.pauseOnHover = this.dataset.pauseHover === 'true';
      this.raf = 0;
      this.lastFrameTime = 0;
      this.loopWidth = 0;
      this._drag = null;
      this._isInteracting = false;
      this._isInViewport = false;

      if (!this.track || this.cards.length < 1) return;

      this.setupLoop();
      this.buildDots();
      this.track.addEventListener('scroll', () => this.updateDots(), {passive:true});
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.activeIndex + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.activeIndex - 1);
        }
      });

      this.track.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.track.addEventListener('pointermove', (event) => this.onPointerMove(event));
      this.track.addEventListener('pointerup', () => this.onPointerEnd());
      this.track.addEventListener('pointercancel', () => this.onPointerEnd());
      this.track.addEventListener('lostpointercapture', () => this.onPointerEnd());

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => this.stopAnimation());
        this.addEventListener('mouseleave', () => this.startAnimation());
        this.addEventListener('focusin', () => this.stopAnimation());
        this.addEventListener('focusout', () => this.startAnimation());
      }

      this._onVisibility = () => {
        if (document.hidden) this.stopAnimation();
        else this.startAnimation();
      };
      document.addEventListener('visibilitychange', this._onVisibility);

      // Avoid running a perpetual animation frame while this carousel is off screen.
      this._intersectionObserver = new IntersectionObserver((entries) => {
        const inViewport = entries.some((entry) => entry.isIntersecting);
        if (inViewport === this._isInViewport) return;
        this._isInViewport = inViewport;
        if (inViewport) this.startAnimation();
        else this.stopAnimation();
      }, { rootMargin: '120px 0px' });
      this._intersectionObserver.observe(this);

      this._resizeObserver = new ResizeObserver(() => {
        this.setupLoop();
        this.buildDots();
        this.updateDots();
        this.startAnimation();
      });
      this._resizeObserver.observe(this.track);
      // Re-measure after the browser finishes the first layout pass and after editor preview renders.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          this.setupLoop();
          this.buildDots();
          this.startAnimation();
        });
      });
      this.startAnimation();
    }

    disconnectedCallback() {
      this.stopAnimation();
      if (this._intersectionObserver) this._intersectionObserver.disconnect();
      this._intersectionObserver = null;
      if (this._resizeObserver) this._resizeObserver.disconnect();
      if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
      if (this.track) {
        this.track.querySelectorAll('[data-benefit-clone="true"]').forEach((clone) => clone.remove());
        this.track.classList.remove('is-autoplaying');
      }
      this._initialized = false;
    }

    get maxScroll() {
      return this.track ? Math.max(0, this.track.scrollWidth - this.track.clientWidth) : 0;
    }

    setupLoop() {
      if (!this.track || !this.cards.length) return;
      this.stopAnimation();
      this.track.querySelectorAll('[data-benefit-clone="true"]').forEach((clone) => clone.remove());
      this.track.classList.remove('is-autoplaying');
      this.loopWidth = 0;
      this.clones = [];

      const viewportWidth = this.track.getBoundingClientRect().width;
      if (!this.autoSlide || this.cards.length < 2 || viewportWidth <= 2) return;

      const firstRect = this.cards[0].getBoundingClientRect();
      const lastRect = this.cards[this.cards.length - 1].getBoundingClientRect();
      const originalWidth = Math.max(0, lastRect.right - firstRect.left + this.cardGap());
      if (originalWidth <= 2) return;

      // Append enough copies that the carousel can keep moving even when all original cards fit on desktop.
      const cloneSets = Math.max(1, Math.ceil(viewportWidth / originalWidth));
      const fragment = document.createDocumentFragment();
      for (let setIndex = 0; setIndex < cloneSets; setIndex++) {
        this.cards.forEach((card) => {
          const clone = card.cloneNode(true);
          clone.dataset.benefitClone = 'true';
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('data-benefit-card');
          clone.removeAttribute('id');
          clone.removeAttribute('data-shopify-editor-block');
          clone.removeAttribute('data-shopify-editor-block-id');
          clone.querySelectorAll('[id], [data-shopify-editor-block], [data-shopify-editor-block-id]').forEach((node) => {
            node.removeAttribute('id');
            node.removeAttribute('data-shopify-editor-block');
            node.removeAttribute('data-shopify-editor-block-id');
          });
          clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => {
            node.setAttribute('tabindex', '-1');
            if (node.matches('a, button')) node.setAttribute('aria-hidden', 'true');
          });
          this.clones.push(clone);
          fragment.appendChild(clone);
        });
      }
      this.track.appendChild(fragment);

      const measureLoop = () => {
        if (!this.isConnected || !this.track || !this.clones.length) return;
        const originalFirst = this.cards[0].getBoundingClientRect();
        const clonedFirst = this.clones[0].getBoundingClientRect();
        this.loopWidth = Math.max(0, clonedFirst.left - originalFirst.left);
        const availableScroll = this.track.scrollWidth - this.track.clientWidth;
        if (this.loopWidth > 2 && availableScroll >= this.loopWidth - 2) {
          this.track.classList.add('is-autoplaying');
          this.buildDots();
          this.updateDots();
          this.startAnimation();
        }
      };

      requestAnimationFrame(() => requestAnimationFrame(measureLoop));
    }

    cardGap() {
      const style = window.getComputedStyle(this.track);
      return parseFloat(style.columnGap || style.gap || '0') || 0;
    }

    cardStep() {
      return this.cards[0] ? this.cards[0].getBoundingClientRect().width + this.cardGap() : 1;
    }

    visibleCards() {
      return Math.max(1, Math.floor((this.track.clientWidth + this.cardGap()) / Math.max(this.cardStep(), 1)));
    }

    pageCount() {
      return Math.max(1, this.cards.length - this.visibleCards() + 1);
    }

    buildDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.replaceChildren();
      const pages = this.pageCount();
      if (pages <= 1 || this.dataset.showDots === 'false' || !this.loopWidth) {
        this.dotsContainer.hidden = true;
        return;
      }
      this.dotsContainer.hidden = false;
      for (let i = 0; i < pages; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'benefits__dot';
        dot.setAttribute('aria-label', 'Go to benefits slide ' + (i + 1));
        dot.addEventListener('click', () => {
          const target = Math.min(this.loopWidth, i * this.cardStep());
          this.track.scrollLeft = target;
          this.updateDots();
        });
        this.dotsContainer.appendChild(dot);
      }
      this.updateDots();
    }

    getLogicalScroll() {
      if (!this.loopWidth) return Math.min(this.track.scrollLeft, this.maxScroll);
      return ((this.track.scrollLeft % this.loopWidth) + this.loopWidth) % this.loopWidth;
    }

    updateDots() {
      if (!this.dotsContainer || !this.track) return;
      const dots = Array.from(this.dotsContainer.querySelectorAll('button'));
      const step = Math.max(this.cardStep(), 1);
      const active = Math.min(dots.length - 1, Math.max(0, Math.round(this.getLogicalScroll() / step)));
      this.activeIndex = active;
      dots.forEach((dot, i) => {
        const isActive = i === active;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    goTo(index) {
      if (!this.track || !this.loopWidth) return;
      const last = Math.max(0, this.pageCount() - 1);
      const targetIndex = Math.min(last, Math.max(0, index));
      this.track.scrollLeft = Math.min(this.loopWidth, targetIndex * this.cardStep());
      this.updateDots();
    }

    startAnimation() {
      this.stopAnimation();
      if (!this.autoSlide || !this.loopWidth || this.cards.length < 2 || document.hidden || !this._isInViewport || this._isInteracting) return;
      if (this.pauseOnHover && (this.matches(':hover') || this.contains(document.activeElement))) return;

      this.track.classList.add('is-autoplaying');
      this.lastFrameTime = 0;
      const tick = (now) => {
        if (!this.isConnected || !this.autoSlide || !this.loopWidth || document.hidden || !this._isInViewport || this._isInteracting) {
          this.raf = 0;
          return;
        }
        if (this.pauseOnHover && (this.matches(':hover') || this.contains(document.activeElement))) {
          this.lastFrameTime = now;
          this.raf = requestAnimationFrame(tick);
          return;
        }
        if (this.lastFrameTime) {
          const delta = Math.min(40, now - this.lastFrameTime);
          // Increase scrollLeft by a fraction of a pixel each frame for smooth, uninterrupted motion.
          this.track.scrollLeft += this.speed * delta / 1000;
          if (this.track.scrollLeft >= this.loopWidth) {
            this.track.scrollLeft -= this.loopWidth;
          }
          this.updateDots();
        }
        this.lastFrameTime = now;
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }

    stopAnimation() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0;
      this.lastFrameTime = 0;
    }

    onPointerDown(event) {
      if (event.target.closest('button, a, input, select, textarea')) return;
      this._isInteracting = true;
      this.stopAnimation();
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      this._drag = {x:event.clientX, scroll:this.track.scrollLeft};
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture?.(event.pointerId);
    }

    onPointerMove(event) {
      if (!this._drag) return;
      this.track.scrollLeft = this._drag.scroll - (event.clientX - this._drag.x);
    }

    onPointerEnd() {
      if (this._drag) {
        this._drag = null;
        this.track.classList.remove('is-dragging');
      }
      if (!this._isInteracting) return;
      this._isInteracting = false;
      if (this.loopWidth && this.track.scrollLeft >= this.loopWidth) this.track.scrollLeft -= this.loopWidth;
      this.updateDots();
      this.startAnimation();
    }
  }

  customElements.define('kidsbazar-benefits', KidsBazarBenefits);
})();